import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Rocket, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { parseNum } from "@/lib/format";
import { Button, Card, Field, Input, Page, PageHeader, StickyFooter } from "@/components/kit";
import { BrandFields, PlanSelect, StatusField, slugify } from "@/components/master/TenantFields";

const EMPTY = { name: "", slug: "", admin_name: "", admin_email: "", admin_password: "", plan_id: null, status: "teste", trial_days: "14", cmv_target: "30", domains: "", contact_phone: "" };

function Result({ r }) {
  const nav = useNavigate();
  const text = `Acesso: ${r.access_url}\nE-mail: ${r.admin_email}${r.admin_password ? `\nSenha: ${r.admin_password}` : ""}`;
  return (
    <Page narrow>
      <Card className="flex flex-col gap-4" data-testid="provision-result">
        <CheckCircle2 size={30} style={{ color: "var(--success)" }} />
        <div className="h2">{r.tenant.name} está no ar</div>
        <p className="caption">Ambiente criado com unidade principal, meta de CMV, marca e administrador. Envie os dados abaixo ao cliente.</p>
        <div className="rounded-xl p-4 text-sm flex flex-col gap-1 tnum" style={{ background: "var(--surface-alt)" }}>
          <div>Acesso: <a href={r.access_url} target="_blank" rel="noreferrer" className="font-bold underline" data-testid="provision-access-url">{r.access_url}</a></div>
          <div>E-mail: <b data-testid="provision-admin-email">{r.admin_email}</b></div>
          {r.admin_password && <div>Senha gerada: <b data-testid="provision-admin-password">{r.admin_password}</b></div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button icon={Copy} variant="secondary" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Copiado"))} data-testid="provision-copy-button">Copiar dados de acesso</Button>
          <Button onClick={() => nav(`/master/tenants/${r.tenant.id}`)} data-testid="provision-open-tenant-button">Ver cliente</Button>
        </div>
      </Card>
    </Page>
  );
}

export default function TenantNew() {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const [brand, setBrand] = useState({});
  const [slugTouched, setSlugTouched] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = useMutation({
    mutationFn: () => api("/master/tenants", { method: "POST", body: {
      ...f, admin_password: f.admin_password || null, trial_days: parseInt(f.trial_days || "0", 10), cmv_target: parseNum(f.cmv_target) || 30,
      domains: f.domains.split(/[,\s]+/).filter(Boolean), brand: { ...brand, app_name: brand.app_name || f.name },
    } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["master-overview"] }),
    onError: (e) => toast.error(e.message),
  });
  if (create.data) return <Result r={create.data} />;
  return (
    <Page>
      <PageHeader title="Novo cliente" subtitle="Cria o ambiente completo e libera o acesso na hora" back="/master" />
      <div className="flex flex-col gap-5 max-w-3xl">
        <Card className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome do restaurante / revenda"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: slugTouched ? f.slug : slugify(e.target.value) })} data-testid="tenant-name-input" /></Field>
          <Field label="Identificador (subdomínio)" hint="Letras minúsculas, números e hífen"><Input value={f.slug} onChange={(e) => { setSlugTouched(true); setF({ ...f, slug: slugify(e.target.value) }); }} data-testid="tenant-slug-input" /></Field>
          <Field label="Domínios próprios (opcional)" hint="Separe por vírgula · ex.: cmv.sangastro.com.br"><Input value={f.domains} onChange={set("domains")} data-testid="tenant-domains-input" /></Field>
          <Field label="Telefone de contato"><Input value={f.contact_phone} onChange={set("contact_phone")} data-testid="tenant-phone-input" /></Field>
        </Card>
        <Card className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 h3">Assinatura e padrões</div>
          <Field label="Plano"><PlanSelect value={f.plan_id} onChange={(v) => setF({ ...f, plan_id: v })} /></Field>
          <Field label="Status inicial"><StatusField value={f.status} onChange={(v) => setF({ ...f, status: v })} /></Field>
          {f.status === "teste" && <Field label="Dias de teste"><Input inputMode="numeric" value={f.trial_days} onChange={(e) => setF({ ...f, trial_days: e.target.value.replace(/\D/g, "") })} data-testid="tenant-trial-input" /></Field>}
          <Field label="Meta de CMV padrão (%)"><Input inputMode="decimal" value={f.cmv_target} onChange={set("cmv_target")} data-testid="tenant-cmv-input" /></Field>
        </Card>
        <Card className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 h3">Administrador do restaurante</div>
          <Field label="Nome"><Input value={f.admin_name} onChange={set("admin_name")} data-testid="tenant-admin-name-input" /></Field>
          <Field label="E-mail"><Input type="email" value={f.admin_email} onChange={set("admin_email")} data-testid="tenant-admin-email-input" /></Field>
          <Field label="Senha (opcional)" hint="Vazio = o sistema gera uma senha"><Input value={f.admin_password} onChange={set("admin_password")} data-testid="tenant-admin-password-input" /></Field>
        </Card>
        <BrandFields brand={brand} setBrand={setBrand} />
      </div>
      <StickyFooter><Button icon={Rocket} loading={create.isPending} onClick={() => create.mutate()} data-testid="provision-submit-button">Criar e liberar acesso</Button></StickyFooter>
    </Page>
  );
}
